// apps/merchant-v3/src/components/topbar/HelpDropdown.tsx
//
// Phase 6 amend #10 topbar redesign — Help menu surfaced on the
// TopBar. Routes to /support + external docs.
//
// 2026-05-29 add(debug): explicit Helpdesk entry. URL is read from
// VITE_HELPDESK_URL at build time so ops can swap the destination
// (Zendesk, Intercom, custom portal) without code changes. Defaults to
// '#' until configured — the click is a no-op on the default.

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown,
  FileText,
  HelpCircle,
  LifeBuoy,
  LifeBuoy as HelpdeskIcon,
  Mail,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@printway/ui';

const HELPDESK_URL = import.meta.env.VITE_HELPDESK_URL ?? '#';

export function HelpDropdown(): JSX.Element {
  const { t } = useTranslation('common');
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="topbar-help-dropdown"
          aria-label={t('topbar.help.label', { defaultValue: 'Help' })}
          className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-sm text-pw-text-muted hover:bg-pw-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
        >
          <HelpCircle className="h-4 w-4 text-pw-text-muted" aria-hidden />
          <span className="hidden md:inline">{t('topbar.help.label', { defaultValue: 'Help' })}</span>
          <ChevronDown className="h-3 w-3 text-pw-text-muted" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="z-50 min-w-[200px] rounded-md border border-pw-border bg-pw-card p-1 shadow-md"
      >
        <DropdownMenuItem asChild>
          <a
            href={HELPDESK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="topbar-help-helpdesk"
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-pw-text-muted outline-none focus:bg-pw-surface data-[highlighted]:bg-pw-surface"
          >
            <HelpdeskIcon className="h-4 w-4" aria-hidden />
            {t('topbar.help.support', { defaultValue: 'Support / Helpdesk' })}
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            to="/tickets"
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-pw-text-muted outline-none focus:bg-pw-surface data-[highlighted]:bg-pw-surface"
          >
            <LifeBuoy className="h-4 w-4" aria-hidden />
            {t('topbar.help.contact', { defaultValue: 'Contact support' })}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a
            href="https://docs.printway.io"
            target="_blank"
            rel="noopener noreferrer"
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-pw-text-muted outline-none focus:bg-pw-surface data-[highlighted]:bg-pw-surface"
          >
            <FileText className="h-4 w-4" aria-hidden />
            {t('topbar.help.documentation', { defaultValue: 'Documentation' })}
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a
            href="mailto:support@printway.io"
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-pw-text-muted outline-none focus:bg-pw-surface data-[highlighted]:bg-pw-surface"
          >
            <Mail className="h-4 w-4" aria-hidden />
            {t('topbar.help.email', { defaultValue: 'Email us' })}
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default HelpDropdown;
